import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../servicios/firebase'; 
import ModalSeguridad from '../components/ui/ModalSeguridad';

const ConfigPrecios = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Estados para el Modal de Seguridad
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null); 
  const [mensajeModal, setMensajeModal] = useState("");

  // Estados locales para la edición de precios
  const [editandoBase, setEditandoBase] = useState(false);
  const [nuevoPrecioBase, setNuevoPrecioBase] = useState("");
  
  const [editandoRedondo, setEditandoRedondo] = useState(false);
  const [nuevoPrecioRedondo, setNuevoPrecioRedondo] = useState("");

  // 1. Conexión a Firebase en tiempo real
  useEffect(() => {
    const docRef = doc(db, 'configuraciones', 'precios');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const info = docSnap.data();
        setData(info);
        // Sincronizamos los estados locales de edición
        setNuevoPrecioBase(info.precioBase);
        setNuevoPrecioRedondo(info.precioRedondo);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // --- LÓGICA DE ACTUALIZACIÓN ---

  // A) Para cambios CRÍTICOS -> Pide Modal
  const solicitarCambioCritico = (campo, valor, mensaje) => {
    setPendingUpdate({ [campo]: valor });
    setMensajeModal(mensaje);
    setModalOpen(true);
  };

  // B) Para cambios MENORES (Días individuales) -> Guardado Directo
  const actualizarDirecto = async (campo, valor) => {
    try {
      const docRef = doc(db, 'configuraciones', 'precios');
      await updateDoc(docRef, { [campo]: valor });
    } catch (error) {
      console.error("Error actualización silenciosa:", error);
      alert("Error al guardar selección de días.");
    }
  };

  // Confirmación final del Modal
  const confirmarCambio = async () => {
    if (!pendingUpdate) return;
    try {
      const docRef = doc(db, 'configuraciones', 'precios');
      await updateDoc(docRef, pendingUpdate);
      
      // Limpieza y cierre de editores
      setModalOpen(false);
      setPendingUpdate(null);
      setEditandoBase(false);
      setEditandoRedondo(false);
    } catch (error) {
      console.error("Error:", error);
      alert("Error al guardar cambios");
    }
  };

  // --- MANEJADORES DE PRECIOS ---

  const handleGuardarPrecioBase = () => {
    const precioNum = Number(nuevoPrecioBase);
    if (!precioNum || precioNum <= 0) return alert("Ingresa un precio válido");

    if (precioNum !== data.precioBase) {
      solicitarCambioCritico(
        'precioBase', 
        precioNum, 
        `Vas a cambiar el PRECIO ESTÁNDAR de $${data.precioBase} a $${precioNum}.`
      );
    } else {
      setEditandoBase(false);
    }
  };

  const handleGuardarPrecioRedondo = () => {
    const precioNum = Number(nuevoPrecioRedondo);
    if (!precioNum || precioNum <= 0) return alert("Ingresa un precio válido");

    if (precioNum !== data.precioRedondo) {
      solicitarCambioCritico(
        'precioRedondo', 
        precioNum, 
        `Vas a cambiar el PRECIO REDONDO de $${data.precioRedondo} a $${precioNum}.`
      );
    } else {
      setEditandoRedondo(false);
    }
  };

  // --- MANEJADORES DE INTERFAZ ---
  const handleCambioRegla = (nuevaRegla) => {
    if (data.regla === nuevaRegla) return;
    let textoRegla = "";
    if (nuevaRegla === 'manual') textoRegla = "CONTROL MANUAL";
    if (nuevaRegla === 'fin_semana') textoRegla = "AUTOMÁTICO FIN DE SEMANA";
    if (nuevaRegla === 'dias_especificos') textoRegla = "DÍAS PERSONALIZADOS";

    solicitarCambioCritico('regla', nuevaRegla, `Vas a cambiar la lógica de precios a: ${textoRegla}.`);
  };

  const handleToggleDia = (diaIndex) => {
    let diasActuales = data.diasPromocion || [];
    if (diasActuales.includes(diaIndex)) {
      diasActuales = diasActuales.filter(d => d !== diaIndex);
    } else {
      diasActuales = [...diasActuales, diaIndex];
    }
    actualizarDirecto('diasPromocion', diasActuales);
  };

  if (loading) return <div className="p-10 text-center">Cargando panel...</div>;
  if (!data) return <div className="p-10 text-red-600">Error: Falta colección configuraciones/precios</div>;

  const diasSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

  return (
    <div className="p-6 max-w-5xl mx-auto bg-gray-50 min-h-screen pb-20">
      
      {/* HEADER CON BOTÓN DE REGRESAR */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Estrategia de Precios</h1>
          <p className="text-gray-500">Gestión de tarifas y reglas de automatización.</p>
        </div>
        <button 
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-100 hover:text-blue-600 transition-colors shadow-sm"
        >
          ⬅ Volver al Inicio
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* PANEL IZQUIERDO: VISUALIZACIÓN Y EDICIÓN DE PRECIOS */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-700 border-b pb-2">Tarifas Base</h2>
          
          {/* 1. PRECIO ESTÁNDAR (Editable) */}
          <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg border border-blue-100 relative">
            <div className="w-full">
              <p className="text-sm text-blue-600 font-bold uppercase mb-1">Precio Estándar</p>
              {!editandoBase ? (
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-extrabold text-gray-800">${data.precioBase}</span>
                  <button onClick={() => setEditandoBase(true)} className="text-gray-400 hover:text-blue-600 mb-1 transition-colors">✏️</button>
                  <p className="text-sm text-gray-600 mb-1.5"> Editar precio </p>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xl font-bold text-gray-600">$</span>
                  <input 
                    type="number" 
                    value={nuevoPrecioBase}
                    onChange={(e) => setNuevoPrecioBase(e.target.value)}
                    className="w-24 p-1 border border-blue-300 rounded font-bold text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                  <button onClick={handleGuardarPrecioBase} className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-bold hover:bg-blue-700">OK</button>
                  <button onClick={() => { setEditandoBase(false); setNuevoPrecioBase(data.precioBase); }} className="text-gray-500 px-2 hover:text-red-500">✕</button>
                </div>
              )}
            </div>
            <span className="text-3xl absolute right-4 top-4 opacity-20">🚐</span>
          </div>

          {/* 2. PRECIO REDONDO (Editable) */}
          <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg border border-purple-100 relative">
            <div className="w-full">
              <p className="text-sm text-purple-600 font-bold uppercase mb-1">Viaje Redondo</p>
              {!editandoRedondo ? (
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-extrabold text-gray-800">${data.precioRedondo}</span>
                  <button onClick={() => setEditandoRedondo(true)} className="text-gray-400 hover:text-purple-600 mb-1 transition-colors">✏️</button>
                  <p className="text-sm text-gray-600 mb-1.5"> Editar precio </p>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xl font-bold text-gray-600">$</span>
                  <input 
                    type="number" 
                    value={nuevoPrecioRedondo}
                    onChange={(e) => setNuevoPrecioRedondo(e.target.value)}
                    className="w-24 p-1 border border-purple-300 rounded font-bold text-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    autoFocus
                  />
                  <button onClick={handleGuardarPrecioRedondo} className="bg-purple-600 text-white px-3 py-1 rounded text-sm font-bold hover:bg-purple-700">OK</button>
                  <button onClick={() => { setEditandoRedondo(false); setNuevoPrecioRedondo(data.precioRedondo); }} className="text-gray-500 px-2 hover:text-red-500">✕</button>
                </div>
              )}
            </div>
            <span className="text-3xl absolute right-4 top-4 opacity-20">🔄</span>
          </div>
        </div>
      </div>

      <ModalSeguridad
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={confirmarCambio}
        title="⚠️ Confirmar Cambio Crítico"
        message={mensajeModal}
        actionType="warning"
      />
    </div>
  );
};

export default ConfigPrecios;